// Purpose: /dropboxes — list of creator's dropboxes (cards).
// Phase 7.5 port: PageHeader + Card grid + Badge tones + EmptyState.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listDropboxes, type DropboxSummary } from '../api/dropbox.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { PlusIcon } from '../components/icons/index.tsx';
import { PageHeader } from '../components/nav/PageHeader.tsx';
import { Badge, Button, Card, EmptyState } from '../components/ui/index.ts';

function formatBytes(n: string): string {
  const bytes = Number(n);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function dropboxBadge(d: DropboxSummary) {
  switch (d.status) {
    case 'ACTIVE':
      return <Badge tone="info">Active</Badge>;
    case 'PENDING_APPROVAL':
      return <Badge tone="warning">Pending approval</Badge>;
    case 'EXPIRED':
      return <Badge tone="neutral">Expired</Badge>;
    case 'REVOKED':
      return <Badge tone="danger">Revoked</Badge>;
  }
}

export function Dropboxes() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [dropboxes, setDropboxes] = useState<DropboxSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') return;
    listDropboxes()
      .then(setDropboxes)
      .catch((err) => setError((err as Error).message));
  }, [status]);

  if (status === 'loading') return <p className="text-body-m text-fg-muted">Loading…</p>;
  if (status === 'unauthenticated') {
    return (
      <p className="text-body-m text-fg-secondary">
        Please{' '}
        <Link to="/login" className="text-accent-primary underline">
          sign in
        </Link>{' '}
        to manage dropboxes.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Your dropboxes"
        subtitle="Collect files from others through a one-time or persistent upload link."
        actions={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => navigate('/dropboxes/new/persistent')}
            >
              Request persistent
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<PlusIcon size={16} strokeWidth={2.5} />}
              onClick={() => navigate('/dropboxes/new/ephemeral')}
            >
              New ephemeral
            </Button>
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {dropboxes === null ? (
        <p className="text-body-m text-fg-muted">Loading dropboxes…</p>
      ) : dropboxes.length === 0 ? (
        <EmptyState
          title="No dropboxes yet"
          description="Create an ephemeral one for a single use, or request a persistent one (admin approval) for HR-style ongoing intake."
          action={
            <Button
              variant="primary"
              leftIcon={<PlusIcon size={16} strokeWidth={2.5} />}
              onClick={() => navigate('/dropboxes/new/ephemeral')}
            >
              New ephemeral
            </Button>
          }
        />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dropboxes.map((d) => (
            <li key={d.id}>
              <Card padding="compact">
                <div className="flex items-start justify-between mb-2 gap-3">
                  <Link
                    to={`/dropboxes/${d.id}`}
                    className="text-body-m font-semibold text-fg-primary hover:underline truncate"
                  >
                    {d.label}
                  </Link>
                  {dropboxBadge(d)}
                </div>
                <div className="text-body-s text-fg-muted space-y-1">
                  <div>
                    <span className="font-medium text-fg-secondary">
                      {d.type === 'EPHEMERAL' ? 'Ephemeral' : 'Persistent'}
                    </span>
                    {' · '}
                    {d.fileCount}/{d.maxFiles} files · {formatBytes(d.maxSizeBytes)} cap
                  </div>
                  {d.expiresAt ? (
                    <div>Expires {new Date(d.expiresAt).toLocaleString()}</div>
                  ) : (
                    <div>No expiry set</div>
                  )}
                  {d.rotatedFromId ? (
                    <div className="text-status-warning">Rotated from earlier dropbox</div>
                  ) : null}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
